import Enumerable from "linq";
import { useCallback } from "react";
import { useQuery } from "react-query";
import { RestError } from "../../services/rest-service/RestService";
import { UserAndRoleAdministrationUsers } from "../../services/user-role-administration-service/Models";
import UserRoleAdministrationService from "../../services/user-role-administration-service/UserRoleAdministrationService";
import { BaseUseQueryParameters } from "../BaseUseQueryParameters";
import { keys } from "./Keys";
import i18n from "../../services/localization-service/i18n";

export enum UserSortEnum {
    LastNameAsc = 0,
    LastNameDesc = 1,
    FirstNameAsc = 2,
    FirstNameDesc = 3,
    UserEmailAsc = 4,
    UserEmailDesc = 5,
    LastActivityAsc = 6,
    LastActivityDesc = 7,
    MobileAccessAsc = 8,
    MobileAccessDesc = 9,
    UserEnabledAsc = 10,
    UserEnabledDesc = 11,
}

export type UseUsersOptionsType = {
    filterText?: string;
    sortBy?: UserSortEnum;
    filterRoleIDs?: Array<number>;
    excludeDisabled?: boolean;
};

export type UseUsersParameters = {
    groupID: number;
    organizationID: number;
    options?: UseUsersOptionsType;
} & BaseUseQueryParameters<UseUsersReturnType>;

export type UseUsersReturnType = {
    data: UserAndRoleAdministrationUsers;
    totalCount: number;
    filteredCount: number;
};

export function useUsers({
    groupID,
    organizationID,
    options,
    enabled,
    onError,
    onSuccess
}: UseUsersParameters) {
    return useQuery<UserAndRoleAdministrationUsers, RestError, UseUsersReturnType>(
        keys.userList(organizationID),
        ({ signal }) => {
            return UserRoleAdministrationService.getUsers(groupID, organizationID, signal);
        },
        {
            select: useCallback(
                (data: UserAndRoleAdministrationUsers) => selectUsers(data, options),
                [options]
            ),
            enabled,
            onError,
            onSuccess
        }
    );
}

function selectUsers(data: UserAndRoleAdministrationUsers, options?: UseUsersOptionsType): UseUsersReturnType {
    const users = data.users.map(u => {
        return {
            ...u,
            firstName: (u.firstName?.length ?? 0) > 0
                ? u.firstName
                : i18n.t("noFirstName"),
            lastName: (u.lastName?.length ?? 0) > 0
                ? u.lastName
                : i18n.t("noLastName"),
            login: (u.login?.length ?? 0) > 0
                ? u.login
                : i18n.t("noLogin"),
        };
    });
    const filterText = options?.filterText?.toLowerCase() ?? "";

    // Filter users
    let query = filterText && filterText.length > 0
        ? Enumerable
            .from(users)
            .where(user => {
                return (user.lastName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                    (user.firstName?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                    (user.login?.toLowerCase().indexOf(filterText) ?? -1) !== -1;
            })
        : Enumerable.from(users);

    if (
        options?.filterRoleIDs &&
        options.filterRoleIDs.length > 0 &&
        options.filterRoleIDs[0] !== -1
    ) {
        query = query.where(user =>
            user.associatedRoleIDs?.some(role =>
                options.filterRoleIDs?.includes(role) ?? false
            ) ?? false
        );
    }

    // Sort users
    switch (options?.sortBy) {
        case UserSortEnum.LastNameAsc:
            query = query
                .orderBy(u => u.lastName?.toLowerCase())
                .thenBy(u => u.firstName?.toLowerCase());
            break;

        case UserSortEnum.LastNameDesc:
            query = query
                .orderByDescending(u => u.lastName?.toLowerCase())
                .thenBy(u => u.firstName?.toLowerCase());
            break;

        case UserSortEnum.FirstNameAsc:
            query = query
                .orderBy(u => u.firstName?.toLowerCase())
                .thenBy(u => u.lastName?.toLowerCase());
            break;

        case UserSortEnum.FirstNameDesc:
            query = query
                .orderByDescending(u => u.firstName?.toLowerCase())
                .thenBy(u => u.lastName?.toLowerCase());
            break;

        case UserSortEnum.UserEmailAsc:
            query = query
                .orderBy(u => u.login?.toLowerCase());
            break;

        case UserSortEnum.UserEmailDesc:
            query = query
                .orderByDescending(u => u.login?.toLowerCase());
            break;

        case UserSortEnum.LastActivityAsc:
            query = query
                .orderBy(u => u.lastActivityTimestamp ?? "")
                .thenBy(u => u.lastName?.toLowerCase());
            break;

        case UserSortEnum.LastActivityDesc:
            query = query
                .orderByDescending(u => u.lastActivityTimestamp ?? "")
                .thenBy(u => u.lastName?.toLowerCase());
            break;

        case UserSortEnum.MobileAccessAsc:
            query = query
                .orderBy(u => !!u.isMobileAccessEnabled)
                .thenBy(u => u.lastName?.toLowerCase());
            break;

        case UserSortEnum.MobileAccessDesc:
            query = query
                .orderByDescending(u => !!u.isMobileAccessEnabled)
                .thenBy(u => u.lastName?.toLowerCase());
            break;

        case UserSortEnum.UserEnabledAsc:
            query = query
                .orderBy(u => !u.isDisabled)
                .thenBy(u => u.lastName?.toLowerCase());
            break;

        case UserSortEnum.UserEnabledDesc:
            query = query
                .orderByDescending(u => !u.isDisabled)
                .thenBy(u => u.lastName?.toLowerCase());
            break;
    }

    if(options?.excludeDisabled){
        query = query.where(r => !r.isDisabled);
    }

    const filteredAndSortedUsers = query.toArray();

    return {
        data: {
            ...data,
            users: filteredAndSortedUsers
        },
        totalCount: users.length,
        filteredCount: filteredAndSortedUsers.length
    };
}